// frontend/src/pages/AuditLogsPage.tsx - Audit Logs Viewer
// Enterprise-grade audit log viewing with filtering and search

import React, { useState, useCallback, useMemo, memo } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { cn, formatDate } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import apiClient from '@/api';

// ===== INTERFACES =====

interface AuditLog {
  _id: string;
  action: string;
  resourceType: string;
  resourceId?: string;
  resourceName?: string;
  userId?: {
    _id: string;
    username: string;
    email: string;
    role: string;
  };
  userSnapshot?: {
    username: string;
    email: string;
    role: string;
  };
  ipAddress?: string;
  userAgent?: string;
  requestId?: string;
  httpMethod?: string;
  endpoint?: string;
  status: 'SUCCESS' | 'FAILURE' | 'PARTIAL';
  errorMessage?: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  metadata?: any;
  tags?: string[];
  createdAt: string;
  duration?: number;
}

interface Filters {
  action: string;
  resourceType: string;
  userId: string;
  status: string;
  severity: string;
  startDate: string;
  endDate: string;
  search: string;
}

// ===== CONSTANTS =====

const ACTION_TYPES = [
  'All Actions',
  'CREATE', 'UPDATE', 'DELETE', 'READ',
  'LOGIN', 'LOGOUT', 'LOGIN_FAILED',
  'USER_ACTIVATE', 'USER_DEACTIVATE',
  'ORG_CREATE', 'ORG_UPDATE', 'ORG_DELETE',
  'ADMIN_BACKUP', 'ADMIN_CACHE_CLEAR', 'ADMIN_EMAIL_SYNC',
  'ACCESS_GRANTED', 'ACCESS_DENIED'
];

const RESOURCE_TYPES = [
  'All Resources',
  'USER', 'ORGANIZATION', 'GROUP', 'PROJECT', 'WORKFLOW',
  'CORRESPONDENCE', 'RESPONSE', 'EMAIL', 'ATTACHMENT',
  'ADMIN', 'SYSTEM', 'AUTH'
];

const STATUS_OPTIONS = ['All', 'SUCCESS', 'FAILURE', 'PARTIAL'];
const SEVERITY_OPTIONS = ['All', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

// ===== UTILITY COMPONENTS =====

const AuditLogCard: React.FC<{
  log: AuditLog;
  onViewDetails: (log: AuditLog) => void;
}> = memo(({ log, onViewDetails }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCESS': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'FAILURE': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'PARTIAL': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'HIGH': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'LOW': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getActionIcon = (action: string) => {
    if (action.includes('CREATE')) return '➕';
    if (action.includes('UPDATE')) return '✏️';
    if (action.includes('DELETE')) return '🗑️';
    if (action.includes('LOGIN')) return '🔐';
    if (action.includes('LOGOUT')) return '🚪';
    if (action.includes('ACCESS_DENIED')) return '🚫';
    if (action.includes('ADMIN')) return '⚙️';
    return '📝';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{getActionIcon(log.action)}</span>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {log.action.replace(/_/g, ' ')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {log.resourceType} {log.resourceName && `• ${log.resourceName}`}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end space-y-1">
          <span className={cn('px-2 py-1 rounded-full text-xs font-medium', getStatusColor(log.status))}>
            {log.status}
          </span>
          <span className={cn('px-2 py-1 rounded-full text-xs font-medium', getSeverityColor(log.severity))}>
            {log.severity}
          </span>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
          <span className="font-medium">User:</span>
          <span>
            {log.userId?.username || log.userSnapshot?.username || 'System'}
            {log.userId?.role && ` (${log.userId.role})`}
          </span>
        </div>

        <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
          <span className="font-medium">Time:</span>
          <span>{formatDate(log.createdAt)}</span>
        </div>

        {log.ipAddress && (
          <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <span className="font-medium">IP:</span>
            <span className="font-mono text-xs">{log.ipAddress}</span>
          </div>
        )}

        {log.errorMessage && (
          <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/10 rounded border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-700 dark:text-red-400">{log.errorMessage}</p>
          </div>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewDetails(log)}
          className="w-full text-xs"
        >
          View Details
        </Button>
      </div>
    </div>
  );
});

const AuditLogDetailsModal: React.FC<{
  log: AuditLog | null;
  onClose: () => void;
}> = ({ log, onClose }) => {
  if (!log) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Audit Log Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Action
                </label>
                <p className="text-sm text-gray-900 dark:text-white font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded">
                  {log.action}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Resource Type
                </label>
                <p className="text-sm text-gray-900 dark:text-white font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded">
                  {log.resourceType}
                </p>
              </div>
            </div>

            {/* User Info */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                User Information
              </label>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                <p className="text-sm text-gray-900 dark:text-white">
                  <span className="font-medium">Username:</span> {log.userId?.username || log.userSnapshot?.username || 'N/A'}
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  <span className="font-medium">Email:</span> {log.userId?.email || log.userSnapshot?.email || 'N/A'}
                </p>
                <p className="text-sm text-gray-900 dark:text-white">
                  <span className="font-medium">Role:</span> {log.userId?.role || log.userSnapshot?.role || 'N/A'}
                </p>
              </div>
            </div>

            {/* Request Info */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Request Information
              </label>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded space-y-1">
                {log.ipAddress && (
                  <p className="text-sm text-gray-900 dark:text-white">
                    <span className="font-medium">IP Address:</span> <span className="font-mono">{log.ipAddress}</span>
                  </p>
                )}
                {log.httpMethod && (
                  <p className="text-sm text-gray-900 dark:text-white">
                    <span className="font-medium">HTTP Method:</span> {log.httpMethod}
                  </p>
                )}
                {log.endpoint && (
                  <p className="text-sm text-gray-900 dark:text-white font-mono">
                    <span className="font-medium">Endpoint:</span> {log.endpoint}
                  </p>
                )}
                {log.requestId && (
                  <p className="text-sm text-gray-900 dark:text-white font-mono text-xs">
                    <span className="font-medium">Request ID:</span> {log.requestId}
                  </p>
                )}
              </div>
            </div>

            {/* Metadata */}
            {log.metadata && Object.keys(log.metadata).length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Metadata
                </label>
                <pre className="text-xs bg-gray-50 dark:bg-gray-700 p-3 rounded overflow-x-auto text-gray-900 dark:text-white">
                  {JSON.stringify(log.metadata, null, 2)}
                </pre>
              </div>
            )}

            {/* Tags */}
            {log.tags && log.tags.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2">
                  {log.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 rounded text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Timestamp */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Timestamp
              </label>
              <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-2 rounded">
                {formatDate(log.createdAt)}
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <Button onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

// ===== MAIN COMPONENT =====

export const AuditLogsPage: React.FC = () => {
  const { user: currentUser } = useAuthStore();
  const { setGlobalError, clearGlobalError } = useAppStore();

  // State
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0, hasMore: false });

  const [filters, setFilters] = useState<Filters>({
    action: '',
    resourceType: '',
    userId: '',
    status: '',
    severity: '',
    startDate: '',
    endDate: '',
    search: ''
  });

  // Check permissions
  const hasAccess = useMemo(() => {
    return currentUser?.role === 'implementor' || currentUser?.role === 'admin';
  }, [currentUser?.role]);

  const isImplementor = currentUser?.role === 'implementor';

  // Fetch logs — implementors see all, admins see their org only
  const fetchLogs = useCallback(async (offset = 0) => {
    if (!hasAccess) return;

    setLoading(true);
    clearGlobalError();

    try {
      const params: any = {
        limit: pagination.limit,
        offset
      };

      if (filters.action && filters.action !== 'All Actions') params.action = filters.action;
      if (filters.resourceType && filters.resourceType !== 'All Resources') params.resourceType = filters.resourceType;
      if (filters.userId) params.userId = filters.userId;
      if (filters.status && filters.status !== 'All') params.status = filters.status;
      if (filters.severity && filters.severity !== 'All') params.severity = filters.severity;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      // Use org-scoped endpoint for admin role, system-wide for implementor
      const result = isImplementor
        ? await apiClient.admin.getActivity(params)
        : await apiClient.admin.getOrgActivity(params);

      setLogs(result.data || []);
      setPagination(result.pagination || { total: 0, limit: 50, offset: 0, hasMore: false });
    } catch (error: any) {
      console.error('Failed to fetch audit logs:', error);
      setGlobalError(error?.response?.data?.message || error?.response?.data?.error || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [hasAccess, isImplementor, filters, pagination.limit, clearGlobalError, setGlobalError]);

  // Initial load
  React.useEffect(() => {
    fetchLogs(0);
  }, []);

  // Handlers
  const handleFilterChange = useCallback((key: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    fetchLogs(0);
  }, [fetchLogs]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      action: '',
      resourceType: '',
      userId: '',
      status: '',
      severity: '',
      startDate: '',
      endDate: '',
      search: ''
    });
  }, []);

  const handleLoadMore = useCallback(() => {
    fetchLogs(pagination.offset + pagination.limit);
  }, [fetchLogs, pagination]);

  // Render guards
  if (!hasAccess) {
    return (
      <PageLayout title="Audit Logs" subtitle="Access denied">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Access Denied
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Only implementors and admins can access audit logs.
          </p>
        </div>
      </PageLayout>
    );
  }

  const pageActions = (
    <div className="flex items-center space-x-2">
      <Button
        variant="outline"
        onClick={() => fetchLogs(pagination.offset)}
        disabled={loading}
      >
        {loading && <LoadingSpinner size="sm" />}
        Refresh
      </Button>
    </div>
  );

  return (
    <PageLayout
      title="Audit Logs"
      subtitle="System activity and security monitoring"
      actions={pageActions}
    >
      <div className="space-y-6">
        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Filters</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Action
              </label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {ACTION_TYPES.map(action => (
                  <option key={action} value={action === 'All Actions' ? '' : action}>
                    {action}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Resource Type
              </label>
              <select
                value={filters.resourceType}
                onChange={(e) => handleFilterChange('resourceType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {RESOURCE_TYPES.map(type => (
                  <option key={type} value={type === 'All Resources' ? '' : type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {STATUS_OPTIONS.map(status => (
                  <option key={status} value={status === 'All' ? '' : status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Severity
              </label>
              <select
                value={filters.severity}
                onChange={(e) => handleFilterChange('severity', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {SEVERITY_OPTIONS.map(severity => (
                  <option key={severity} value={severity === 'All' ? '' : severity}>
                    {severity}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button onClick={handleApplyFilters} disabled={loading}>
              Apply Filters
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>
            Showing {logs.length} of {pagination.total} audit logs
          </span>
        </div>

        {/* Loading State */}
        {loading && logs.length === 0 && (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {/* Audit Logs Grid */}
        {!loading && logs.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">No audit logs found</p>
          </div>
        )}

        {logs.length > 0 && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {logs.map(log => (
                <AuditLogCard
                  key={log._id}
                  log={log}
                  onViewDetails={setSelectedLog}
                />
              ))}
            </div>

            {/* Load More */}
            {pagination.hasMore && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading && <LoadingSpinner size="sm" />}
                  Load More
                </Button>
              </div>
            )}
          </>
        )}

        {/* Details Modal */}
        <AuditLogDetailsModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      </div>
    </PageLayout>
  );
};
